import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  CardContainer,
  LoadingSpinner,
  Select,
  StatusChip,
  useLocalStorageAsState,
} from "@msys/ui";
import {
  Alert,
  Box,
  Checkbox,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from "@mui/material";
import * as Sentry from "@sentry/react";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, type FormikProps } from "formik";
import { useSnackbar } from "notistack";
import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import {
  IntegrationId,
  namedOperations,
} from "../../../../../clients/graphqlTypes.js";
import { BreadcrumbItem, Page } from "../../../../commons/layout/Page.js";
import { PageContainer } from "../../../../commons/layout/PageContainer.js";
import { PasswordField } from "../../../../commons/form-fields/PasswordField.js";
import { TextField } from "../../../../commons/form-fields/TextField.js";
import { useUnsavedChangesGuard } from "../../../../commons/hooks/useUnsavedChangesGuard.js";
import type { IntegrationSettingsProps } from "../../integrationRegistry.js";
import { getIntegrationMeta } from "../../integrationRegistry.js";
import { integrationErrorMessage } from "../../integrationErrorMessage.js";
import { useGenericDashboardQuery } from "../GenericDashboard.generated.js";
import {
  CardSaveRow,
  FormDirtyTracker,
  useCardDirtyRegistry,
} from "../integration-settings/integrationCardForm.js";
import {
  usePdsSyncSettings_AddMutation,
  usePdsSyncSettings_UpdateMutation,
  usePdsSyncSettings_RemoveMutation,
  usePdsSyncSettings_RotateTokenMutation,
} from "./PdsSyncSettings.generated.js";
import {
  INTEGRATIONS_BASE_PATH,
  integrationDashboardPath,
  integrationSettingsPath,
} from "../../paths.js";

// -- Create mode (deep links; the overview card uses ConnectIntegrationModal) --

interface CreateFormValues {
  baseUrl: string;
  accessToken: string;
}

const CreateModeForm = ({
  integrationId,
  meta,
}: {
  integrationId: string;
  meta: ReturnType<typeof getIntegrationMeta>;
}) => {
  const { t } = useTranslate([
    "Integrations",
    "OrganisationPageTopbar",
    "Global",
  ]);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const client = useApolloClient();
  const [addMutation] = usePdsSyncSettings_AddMutation({
    client,
    context: { noErrorNotification: true },
  });

  const createValidationSchema = Yup.object().shape({
    baseUrl: Yup.string()
      .url(t("Must be a valid URL"))
      .required(t("URL is required")),
    accessToken: Yup.string().required(t("Access token is required")),
  });

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("Organisation", { ns: "OrganisationPageTopbar" }),
      to: "/organisation",
    },
    { title: t("Integrations"), to: INTEGRATIONS_BASE_PATH },
    { title: "PDS", to: integrationSettingsPath(integrationId) },
  ];

  return (
    <Page
      title={t("Connect PDS")}
      breadcrumbs={breadcrumbs}
      breadcrumbsSize="medium"
    >
      <PageContainer>
        <Stack spacing={2} sx={{ maxWidth: 480 }}>
          <Typography variant="body2" color="text.secondary">
            {t(meta.description as any)}
          </Typography>

          <Formik<CreateFormValues>
            initialValues={{ baseUrl: "", accessToken: "" }}
            validationSchema={createValidationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                await addMutation({
                  variables: {
                    input: {
                      integrationId: integrationId as IntegrationId,
                      settings: values,
                    },
                  },
                  refetchQueries: [
                    namedOperations.Query.IntegrationsOverview,
                    namedOperations.Query.OrganisationIntegrationsSidebar,
                  ],
                });
                // Land in the PDS settings page after connecting (design
                // review) — not the top-level Tasks page.
                navigate(integrationSettingsPath(integrationId));
              } catch (e) {
                enqueueSnackbar(
                  integrationErrorMessage(
                    e,
                    t("Something went wrong. Please try again.", {
                      ns: "Global",
                    })
                  ),
                  {
                    variant: "error",
                  }
                );
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting }) => (
              <Form>
                <Stack spacing={2}>
                  <TextField
                    name="baseUrl"
                    label={t("PDS Server URL")}
                    placeholder="https://pds.example.com"
                    required
                    disabled={isSubmitting}
                  />
                  <PasswordField name="accessToken" label={t("Access token")} />
                  <Typography variant="caption" color="text.secondary">
                    {t(
                      "You will receive the URL and access token from your PDS contact or administrator."
                    )}
                  </Typography>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? t("Connecting...") : t("Connect")}
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
        </Stack>
      </PageContainer>
    </Page>
  );
};

// -- Edit mode: form state is exactly the user-editable settings subset --

// "review" applies only to PDS→CRM (incoming — review inbox is incoming-only);
// "block" applies only to CRM→PDS (outgoing — MS→PDS sync isn't implemented, so
// the only coherent options are apply-automatically or don't-sync).
type RoutingChoice = "auto" | "review" | "block";

interface SettingsFormValues {
  rollenFilter: string[];
  privatpersonenSyncen: boolean;
  filialenSyncen: boolean;
  routingPdsToCrmCreate: RoutingChoice;
  routingPdsToCrmUpdate: RoutingChoice;
  // Wired end-to-end for a future MS→PDS implementation; the UI keeps these
  // disabled ("coming soon") today.
  routingCrmToPdsCreate: RoutingChoice;
  routingCrmToPdsUpdate: RoutingChoice;
  // MEIS-12747: quote export rule — its own "Angebots-Export" card, ENABLED
  // (the export is implemented, unlike the generic outgoing rules).
  quoteCreate: "auto" | "block";
  fullSyncCronHour: number;
}

type StoredSettings = {
  baseUrl?: string;
  syncConfig?: {
    timing?: { fullSyncCronHour?: number };
    sync_scope?: {
      rollen_filter?: string[];
      privatpersonen_syncen?: boolean;
      filialen_syncen?: boolean;
    };
    routing?: {
      pds_to_crm?: { create?: string; update?: string };
      crm_to_pds?: { create?: string; update?: string; quote_create?: string };
    };
  };
};

// PDS→CRM (incoming): auto | review, default auto. Review is hidden for now
// (PDS_TO_CRM_REVIEW_ENABLED off), so new connections and any stored
// "review"/"block" values display and re-save as "auto". Flipping the flag on
// restores the "review" default with no other change here.
function normalizeIncomingChoice(value: string | undefined): RoutingChoice {
  if (!PDS_TO_CRM_REVIEW_ENABLED) return "auto";
  return value === "auto" ? "auto" : "review";
}

// CRM→PDS (outgoing): auto | block, default block. Wired end-to-end for a
// future MS→PDS implementation; not editable in the UI yet (see
// CRM_TO_PDS_EDITABLE).
function normalizeOutgoingChoice(value: string | undefined): RoutingChoice {
  return value === "auto" ? "auto" : "block";
}

// Quote export (MEIS-12747): auto | block, default AUTO — the export is
// implemented and user-triggered (the click is the approval).
function normalizeQuoteCreateChoice(
  value: string | undefined
): "auto" | "block" {
  return value === "block" ? "block" : "auto";
}

// MS→PDS (CRM→PDS) sync isn't implemented yet (the v2 PDS client is read-only).
// The routing is fully wired — form state, save payload, and the user-subset
// schema all accept it — but the UI keeps the controls disabled with a
// "coming soon" chip. Flip this to true (and the chip disappears) when MS→PDS
// write-back ships; no other change is needed to make it editable.
const CRM_TO_PDS_EDITABLE = false;

// PDS→CRM review is hidden for now (designer review): incoming edits always
// apply automatically. The "review" choice, the review inbox, and the routing
// plumbing all stay intact — flip this to true to re-offer "Review first" on
// the incoming rows. Mirrors the CRM_TO_PDS_EDITABLE pattern above.
const PDS_TO_CRM_REVIEW_ENABLED = false;

const ROLES = [
  { value: "KUNDE", labelKey: "Customer" },
  { value: "LIEFERANT", labelKey: "Supplier" },
  { value: "INTERESSENT", labelKey: "Prospect" },
  { value: "ANSPRECHPARTNER", labelKey: "Contact person" },
];

// Canonical role order — Formik computes dirty via deep equality, so a
// deselect/reselect cycle must reproduce the exact initial array, not a
// reordered one
function sortRoles(roles: string[]): string[] {
  const order = ROLES.map(r => r.value);
  return [...roles].sort((a, b) => order.indexOf(a) - order.indexOf(b));
}

function settingsToFormValues(settings: StoredSettings): SettingsFormValues {
  const sc = settings.syncConfig ?? {};
  const scope = sc.sync_scope ?? {};
  return {
    rollenFilter: sortRoles(
      scope.rollen_filter ?? [
        "KUNDE",
        "LIEFERANT",
        "INTERESSENT",
        "ANSPRECHPARTNER",
      ]
    ),
    privatpersonenSyncen: scope.privatpersonen_syncen ?? true,
    filialenSyncen: scope.filialen_syncen ?? true,
    // Incoming (PDS→CRM) defaults to review; outgoing (CRM→PDS) defaults to
    // block. Both are wired; the UI keeps outgoing disabled until MS→PDS ships.
    routingPdsToCrmCreate: normalizeIncomingChoice(
      sc.routing?.pds_to_crm?.create
    ),
    routingPdsToCrmUpdate: normalizeIncomingChoice(
      sc.routing?.pds_to_crm?.update
    ),
    routingCrmToPdsCreate: normalizeOutgoingChoice(
      sc.routing?.crm_to_pds?.create
    ),
    routingCrmToPdsUpdate: normalizeOutgoingChoice(
      sc.routing?.crm_to_pds?.update
    ),
    quoteCreate: normalizeQuoteCreateChoice(
      sc.routing?.crm_to_pds?.quote_create
    ),
    fullSyncCronHour: sc.timing?.fullSyncCronHour ?? 2,
  };
}

// Per-card form slices (each section card is its own form with its own
// Save/Cancel; a card's save merges its slice over the last saved values,
// so other cards' unsaved edits are never swept along)
type ApprovalValues = Pick<
  SettingsFormValues,
  | "routingPdsToCrmCreate"
  | "routingPdsToCrmUpdate"
  | "routingCrmToPdsCreate"
  | "routingCrmToPdsUpdate"
>;
type ScheduleValues = Pick<SettingsFormValues, "fullSyncCronHour">;
type QuoteExportValues = Pick<SettingsFormValues, "quoteCreate">;
type ScopeValues = Pick<
  SettingsFormValues,
  "rollenFilter" | "privatpersonenSyncen" | "filialenSyncen"
>;

function pickApproval(v: SettingsFormValues): ApprovalValues {
  return {
    routingPdsToCrmCreate: v.routingPdsToCrmCreate,
    routingPdsToCrmUpdate: v.routingPdsToCrmUpdate,
    routingCrmToPdsCreate: v.routingCrmToPdsCreate,
    routingCrmToPdsUpdate: v.routingCrmToPdsUpdate,
  };
}

function pickSchedule(v: SettingsFormValues): ScheduleValues {
  return { fullSyncCronHour: v.fullSyncCronHour };
}

function pickQuoteExport(v: SettingsFormValues): QuoteExportValues {
  return { quoteCreate: v.quoteCreate };
}

function pickScope(v: SettingsFormValues): ScopeValues {
  return {
    rollenFilter: v.rollenFilter,
    privatpersonenSyncen: v.privatpersonenSyncen,
    filialenSyncen: v.filialenSyncen,
  };
}

/**
 * Emits ONLY user-subset keys — the server validates the payload against
 * the strict pdsSyncUserSettingsSchema and deep-merges it over the stored
 * settings, so support-tuned values must never appear here.
 */
function formValuesToSettings(values: SettingsFormValues) {
  return {
    syncConfig: {
      sync_scope: {
        rollen_filter: values.rollenFilter,
        privatpersonen_syncen: values.privatpersonenSyncen,
        filialen_syncen: values.filialenSyncen,
      },
      // Both directions are wired. CRM→PDS is disabled in the UI today, so it
      // stays at its "block" default — but the save path emits it so enabling
      // it later (CRM_TO_PDS_EDITABLE) needs no plumbing change.
      routing: {
        pds_to_crm: {
          create: values.routingPdsToCrmCreate,
          update: values.routingPdsToCrmUpdate,
        },
        crm_to_pds: {
          create: values.routingCrmToPdsCreate,
          update: values.routingCrmToPdsUpdate,
          quote_create: values.quoteCreate,
        },
      },
      timing: {
        fullSyncCronHour: Number(values.fullSyncCronHour),
      },
    },
  };
}

// -- Section card: Wie Änderungen behandelt werden (incl. Vollabgleich) --

function RoutingRow({
  label,
  value,
  onChange,
  choices,
  disabled,
  t,
}: {
  label: string;
  value: RoutingChoice;
  onChange: (v: RoutingChoice) => void;
  // Incoming rows pass ["auto", "review"]; outgoing rows ["auto", "block"].
  choices: RoutingChoice[];
  // Outgoing (MS→PDS) rows are disabled — the feature isn't implemented yet.
  disabled?: boolean;
  t: (key: any) => string;
}) {
  const choiceLabel = (c: RoutingChoice): string =>
    c === "auto"
      ? t("Apply automatically")
      : c === "review"
        ? t("Review first")
        : t("Don't sync");
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ alignItems: "center", justifyContent: "space-between" }}
    >
      <Typography variant="body2" noWrap>
        {label}
      </Typography>
      <Select<RoutingChoice>
        size="extra-small"
        value={value}
        onChange={next => onChange(next)}
        disabled={disabled}
        options={choices.map(c => ({ label: choiceLabel(c), value: c }))}
        // style (not sx) so the width lands on the outer FormControl —
        // sx is forwarded to the inner MuiSelect only
        style={{ width: 220, flexShrink: 0 }}
      />
    </Stack>
  );
}

function RoutingSection({
  fp,
  t,
  integrationId,
}: {
  fp: FormikProps<ApprovalValues>;
  t: (key: any, options?: any) => string;
  integrationId: string;
}) {
  // Onboarding banner: show only right after connecting — while every routing
  // choice is still at its default (read from the stored initial values, not
  // live edits, so it doesn't flicker mid-change). It disappears once the user
  // configures anything away from the default, and a dismiss (X) hides it
  // permanently for users who deliberately keep the defaults.
  // The banner nudges configuring the routing, so it shows only while there is
  // actually an editable routing direction and it is still at its default.
  // Both directions are disabled today (PDS_TO_CRM_REVIEW_ENABLED forces
  // incoming to "auto"; CRM_TO_PDS_EDITABLE fixes outgoing at "block"), so the
  // banner stays hidden until one of those flags is enabled — enabling a flag
  // restores the corresponding default check with no other change here.
  const v = fp.initialValues;
  const isAllDefault =
    (PDS_TO_CRM_REVIEW_ENABLED || CRM_TO_PDS_EDITABLE) &&
    (!PDS_TO_CRM_REVIEW_ENABLED ||
      (v.routingPdsToCrmCreate === "review" &&
        v.routingPdsToCrmUpdate === "review")) &&
    (!CRM_TO_PDS_EDITABLE ||
      (v.routingCrmToPdsCreate === "block" &&
        v.routingCrmToPdsUpdate === "block"));
  const [bannerDismissed, setBannerDismissed] = useLocalStorageAsState(
    `pds-edits-approval-banner-dismissed:${integrationId}`,
    false
  );

  return (
    <CardContainer title={t("Edits approval")}>
      <Stack spacing={2} sx={{ px: 1, pt: 0.5, pb: 1 }}>
        {isAllDefault && !bannerDismissed && (
          <Alert severity="info" onClose={() => setBannerDismissed(true)}>
            {t(
              "Take a minute to set how edits sync between PDS and MeisterSystems — apply them automatically, or review each one first."
            )}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary">
          {t(
            "Decide whether edits are applied automatically or reviewed by you first."
          )}
        </Typography>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t("Edits from PDS")}
          </Typography>
          {/* Review is hidden for now (PDS_TO_CRM_REVIEW_ENABLED off): the
              incoming rows are disabled and fixed at "Apply automatically",
              mirroring the disabled CRM→PDS rows below. The "review" choice
              and inbox stay wired — flip the flag to re-offer it. */}
          <Stack spacing={1.5}>
            <RoutingRow
              label={t("New contacts")}
              value={
                PDS_TO_CRM_REVIEW_ENABLED
                  ? fp.values.routingPdsToCrmCreate
                  : "auto"
              }
              onChange={v => fp.setFieldValue("routingPdsToCrmCreate", v)}
              choices={
                PDS_TO_CRM_REVIEW_ENABLED ? ["auto", "review"] : ["auto"]
              }
              disabled={!PDS_TO_CRM_REVIEW_ENABLED}
              t={t}
            />
            <RoutingRow
              label={t("Edited contacts")}
              value={
                PDS_TO_CRM_REVIEW_ENABLED
                  ? fp.values.routingPdsToCrmUpdate
                  : "auto"
              }
              onChange={v => fp.setFieldValue("routingPdsToCrmUpdate", v)}
              choices={
                PDS_TO_CRM_REVIEW_ENABLED ? ["auto", "review"] : ["auto"]
              }
              disabled={!PDS_TO_CRM_REVIEW_ENABLED}
              t={t}
            />
          </Stack>
        </Box>

        {/* MS→PDS sync isn't implemented yet (the v2 PDS client is read-only).
            The routing is fully wired (form + save payload + schema) but the
            UI stays disabled with a "coming soon" chip — flip
            CRM_TO_PDS_EDITABLE to turn it on, no other change needed. */}
        <Box>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "center", mb: 0.5 }}
          >
            <Typography variant="subtitle2">
              {t("Edits from MeisterSystems to PDS")}
            </Typography>
            {!CRM_TO_PDS_EDITABLE && (
              <Tooltip
                title={t(
                  "Syncing your MeisterSystems edits back to PDS isn't available yet."
                )}
              >
                <Chip label={t("Coming soon")} size="small" />
              </Tooltip>
            )}
          </Stack>
          <Stack spacing={1.5}>
            <RoutingRow
              label={t("New contacts")}
              value={fp.values.routingCrmToPdsCreate}
              onChange={v => fp.setFieldValue("routingCrmToPdsCreate", v)}
              choices={["auto", "block"]}
              disabled={!CRM_TO_PDS_EDITABLE}
              t={t}
            />
            <RoutingRow
              label={t("Edited contacts")}
              value={fp.values.routingCrmToPdsUpdate}
              onChange={v => fp.setFieldValue("routingCrmToPdsUpdate", v)}
              choices={["auto", "block"]}
              disabled={!CRM_TO_PDS_EDITABLE}
              t={t}
            />
          </Stack>
        </Box>

        <CardSaveRow fp={fp} />
      </Stack>
    </CardContainer>
  );
}

// -- Section card: Angebots-Export (MEIS-12747) --
// Deliberately its own card, SEPARATE from the CRM edits-approval card: quote
// exports are user-triggered document transfers, not CRM change sync — and
// unlike the outgoing CRM rows this ships ENABLED (the feature is implemented).
function QuoteExportSection({
  fp,
  t,
}: {
  fp: FormikProps<QuoteExportValues>;
  t: (key: any, options?: any) => string;
}) {
  return (
    <CardContainer title={t("Quote export")}>
      <Stack spacing={2} sx={{ px: 1, pt: 0.5, pb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t("Published quotes can be pushed to PDS from the quote view.")}
        </Typography>
        {/* A plain on/off toggle (not a routing Select): quote export is a
            user-triggered action with no "review" state — mirrors the boolean
            toggles in "Synchronized data". checked = auto, unchecked = block. */}
        <FormControlLabel
          sx={{ alignItems: "flex-start" }}
          control={
            <Checkbox
              size="small"
              sx={{ py: 0.5 }}
              checked={fp.values.quoteCreate === "auto"}
              onChange={e =>
                fp.setFieldValue(
                  "quoteCreate",
                  e.target.checked ? "auto" : "block"
                )
              }
            />
          }
          label={
            <Box>
              <Typography variant="body2">{t("Allow quote export")}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('Adds an "Export to PDS" button on published quotes.')}
              </Typography>
            </Box>
          }
        />
        <CardSaveRow fp={fp} />
      </Stack>
    </CardContainer>
  );
}

// -- Section card: Zeitplan --

function ScheduleSection({
  fp,
  t,
}: {
  fp: FormikProps<ScheduleValues>;
  t: (key: any) => string;
}) {
  return (
    <CardContainer title={t("Schedule")}>
      <Stack spacing={0} sx={{ px: 1, pt: 0.5, pb: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {t("Full sync at")}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1 }}
        >
          {t(
            "Time (local time). The entire dataset is synced once a day — preferably at night."
          )}
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
          {[0, 1, 2, 3, 4, 5, 6].map(h => (
            <Chip
              key={h}
              label={`${h}:00`}
              onClick={() => fp.setFieldValue("fullSyncCronHour", h)}
              color={fp.values.fullSyncCronHour === h ? "primary" : "default"}
              variant={fp.values.fullSyncCronHour === h ? "filled" : "outlined"}
            />
          ))}
        </Stack>
        <CardSaveRow fp={fp} />
      </Stack>
    </CardContainer>
  );
}

// -- Section card: Synchronisierte Daten --

function SyncScopeSection({
  fp,
  t,
}: {
  fp: FormikProps<ScopeValues>;
  t: (key: any) => string;
}) {
  const toggleRole = (value: string) => {
    const current = fp.values.rollenFilter;
    const next = current.includes(value)
      ? current.filter(r => r !== value)
      : sortRoles([...current, value]);
    fp.setFieldValue("rollenFilter", next, true);
    fp.setFieldTouched("rollenFilter", true, false);
  };

  return (
    <CardContainer title={t("Synchronized data")}>
      <Stack spacing={2} sx={{ px: 1, pt: 0.5, pb: 1 }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {t("Contact types")}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            {t(
              "Only the selected contact types are synced. Contacts that were already transferred stay in MeisterSystems."
            )}
          </Typography>
          {/* Compact rows, mirroring the switch sections on the SAP page
              (e.g. "UI Toggles") — reduced checkbox padding keeps the list
              narrow */}
          <Stack spacing={0}>
            {ROLES.map(r => (
              <FormControlLabel
                key={r.value}
                control={
                  <Checkbox
                    size="small"
                    sx={{ py: 0.5 }}
                    checked={fp.values.rollenFilter.includes(r.value)}
                    onChange={() => toggleRole(r.value)}
                  />
                }
                label={t(r.labelKey as any)}
              />
            ))}
          </Stack>
          {typeof fp.errors.rollenFilter === "string" && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: "block", mt: 1 }}
            >
              {fp.errors.rollenFilter}
            </Typography>
          )}
        </Box>

        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
            {t("Additional data")}
          </Typography>
          <Stack spacing={0.5}>
            <FormControlLabel
              sx={{ alignItems: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  sx={{ py: 0.5 }}
                  checked={fp.values.privatpersonenSyncen}
                  onChange={e =>
                    fp.setFieldValue("privatpersonenSyncen", e.target.checked)
                  }
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {t("Include private persons")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("Private contacts from PDS")}
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              sx={{ alignItems: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  sx={{ py: 0.5 }}
                  checked={fp.values.filialenSyncen}
                  onChange={e =>
                    fp.setFieldValue("filialenSyncen", e.target.checked)
                  }
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{t("Sync branches")}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("Branches and locations")}
                  </Typography>
                </Box>
              }
            />
          </Stack>
        </Box>

        <CardSaveRow fp={fp} />
      </Stack>
    </CardContainer>
  );
}

// -- Compact status header (API status chip + last sync + dashboard link) --

// Tinted chip colors per health state, mirroring the overview card
// (IntegrationCard.tsx) so the same health state always reads the same color.
const healthChipColors: Record<string, { bg: string; text: string }> = {
  ok: { bg: "#e8f5e9", text: "#2e7d32" },
  degraded: { bg: "#fff3e0", text: "#e65100" },
  unreachable: { bg: "#ffebee", text: "#c62828" },
};
const fallbackHealthChip = { bg: "#fff3e0", text: "#e65100" };

function StatusHeader({
  health,
  integrationId,
  t,
}: {
  health: { health: string; updatedAt?: string | null } | null;
  integrationId: string;
  t: (key: any, options?: any) => string;
}) {
  const navigate = useNavigate();

  const healthLabel = !health
    ? t("Connecting...")
    : health.health === "ok"
      ? t("OK", { ns: "Global" })
      : health.health === "degraded"
        ? t("Slow")
        : health.health === "unreachable"
          ? t("Unreachable")
          : t("Connecting...");
  const healthChip = health
    ? (healthChipColors[health.health] ?? fallbackHealthChip)
    : fallbackHealthChip;

  const relativeTime = React.useMemo(() => {
    if (!health?.updatedAt) return null;
    const diff = Date.now() - new Date(health.updatedAt).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return t("just now");
    if (minutes < 60)
      return t("{minutes} min ago", { minutes, ns: "Integrations" });
    const hours = Math.floor(minutes / 60);
    return t("{hours} h ago", { hours, ns: "Integrations" });
  }, [health?.updatedAt, t]);

  return (
    <Paper
      sx={{
        p: 1.5,
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: "center", justifyContent: "space-between" }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <StatusChip
            label={healthLabel}
            size="small"
            backgroundColor={healthChip.bg}
            textColor={healthChip.text}
          />
          {relativeTime && (
            <Typography variant="body2" color="text.secondary">
              {t("Last sync")}: {relativeTime}
            </Typography>
          )}
        </Stack>
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate(integrationDashboardPath(integrationId))}
        >
          {t("Go to dashboard")}
        </Button>
      </Stack>
    </Paper>
  );
}

// -- Section card: Verbindung (immediate actions, separate from form save) --

export type ConnectionDiagnostics = {
  apiVersion: string | null;
  apiVersionStatus: string | null;
  latencyMs: number | null;
};

const versionStatusLabels: Record<string, string> = {
  verified: "Verified",
  failed: "Not verified",
  unsupported: "Not supported",
  open: "Unknown",
  unknown: "Unknown",
};

function ConnectionCard({
  baseUrl,
  newToken,
  setNewToken,
  onRotateToken,
  diagnostics,
  t,
}: {
  baseUrl: string;
  newToken: string;
  setNewToken: (v: string) => void;
  onRotateToken: () => void;
  diagnostics: ConnectionDiagnostics | null;
  t: (key: any) => string;
}) {
  const statusLabel =
    diagnostics?.apiVersionStatus != null
      ? t(versionStatusLabels[diagnostics.apiVersionStatus] ?? "Unknown")
      : null;
  return (
    <CardContainer title={t("Connection")}>
      <Stack spacing={2} sx={{ px: 1, pt: 0.5, pb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {t("Changes here apply immediately.")}
        </Typography>
        <MuiTextField
          label={t("PDS Server URL")}
          value={baseUrl}
          disabled
          size="small"
          fullWidth
          slotProps={{
            input: { sx: { fontFamily: "monospace", fontSize: 13 } },
          }}
        />
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <MuiTextField
            label={t("New token")}
            type="password"
            autoComplete="off"
            value={newToken}
            onChange={e => setNewToken(e.target.value)}
            size="small"
            fullWidth
          />
          <Button
            variant="outlined"
            size="small"
            onClick={onRotateToken}
            disabled={!newToken}
            sx={{ whiteSpace: "nowrap", px: 2 }}
          >
            {t("Rotate token")}
          </Button>
        </Stack>
        {diagnostics && (
          <Stack direction="row" spacing={4}>
            {diagnostics.apiVersion && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                >
                  {t("PDS version")}
                </Typography>
                <Typography variant="body2">
                  {diagnostics.apiVersion}
                  {statusLabel ? ` (${statusLabel})` : ""}
                </Typography>
              </Box>
            )}
            {diagnostics.latencyMs !== null && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                >
                  {t("Latency")}
                </Typography>
                <Typography variant="body2">
                  {diagnostics.latencyMs} ms
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </Stack>
    </CardContainer>
  );
}

// -- Main component --

export const PdsSyncSettings = ({
  integrationId,
  mode,
}: IntegrationSettingsProps) => {
  const { t } = useTranslate([
    "Integrations",
    "OrganisationPageTopbar",
    "Global",
  ]);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const client = useApolloClient();
  const meta = getIntegrationMeta(integrationId);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [newToken, setNewToken] = React.useState("");
  const { anyDirty, dirtyOf } = useCardDirtyRegistry();

  useUnsavedChangesGuard(
    anyDirty,
    t("You have unsaved changes. Leave the page anyway?")
  );

  const { data, loading } = useGenericDashboardQuery({
    client,
    variables: { integrationId: integrationId as IntegrationId },
    fetchPolicy: "cache-and-network",
    skip: mode === "create",
  });

  const [updateMutation] = usePdsSyncSettings_UpdateMutation({
    client,
    context: { noErrorNotification: true },
  });
  const [removeMutation, { loading: removeLoading }] =
    usePdsSyncSettings_RemoveMutation({
      client,
      context: { noErrorNotification: true },
    });
  const [rotateTokenMutation] = usePdsSyncSettings_RotateTokenMutation({
    client,
    context: { noErrorNotification: true },
  });

  const orgIntegration = getDataOrNull(
    data?.organisationIntegration
  )?.organisationIntegration;

  const scopeValidationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        rollenFilter: Yup.array()
          .of(Yup.string())
          .min(1, t("At least one contact type must be selected.")),
      }),
    [t]
  );
  const scheduleValidationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        fullSyncCronHour: Yup.number().min(0).max(23).required(),
      }),
    []
  );

  if (mode === "create") {
    return <CreateModeForm integrationId={integrationId} meta={meta} />;
  }

  if (loading && !orgIntegration) return <LoadingSpinner />;
  if (!orgIntegration) {
    return <Navigate to={INTEGRATIONS_BASE_PATH} replace />;
  }

  const storedSettings = (orgIntegration.settings ?? {}) as StoredSettings;
  const initialValues = settingsToFormValues(storedSettings);

  // Health details are free-form JSON — narrow the fields the Connection
  // card shows instead of trusting the shape
  const healthDetails: unknown = orgIntegration.health?.details;
  let diagnostics: ConnectionDiagnostics | null = null;
  if (healthDetails && typeof healthDetails === "object") {
    const rec = healthDetails as Record<string, unknown>;
    const apiVersion =
      typeof rec.apiVersion === "string" ? rec.apiVersion : null;
    const apiVersionStatus =
      typeof rec.apiVersionStatus === "string" ? rec.apiVersionStatus : null;
    const latencyMs = typeof rec.latencyMs === "number" ? rec.latencyMs : null;
    if (apiVersion !== null || latencyMs !== null) {
      diagnostics = { apiVersion, apiVersionStatus, latencyMs };
    }
  }

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("Organisation", { ns: "OrganisationPageTopbar" }),
      to: "/organisation",
    },
    { title: t("Integrations"), to: INTEGRATIONS_BASE_PATH },
    { title: "PDS", to: integrationSettingsPath(integrationId) },
  ];

  const handleSave = async (values: SettingsFormValues) => {
    try {
      await updateMutation({
        variables: {
          input: {
            integrationId: integrationId as IntegrationId,
            settings: formValuesToSettings(values),
          },
        },
      });
      await client.refetchQueries({
        include: [namedOperations.Query.GenericDashboard],
      });
      enqueueSnackbar(t("Settings saved"), { variant: "success" });
    } catch (e) {
      // A subset-validation rejection implies a client bug: never pipe the
      // raw error into the snackbar. Sentry gets tags only — the submitted
      // settings object may carry misplaced secrets (PII/token risk).
      Sentry.captureException(e, {
        tags: { integration: "pds_sync", kind: "settings-subset-rejection" },
      });
      enqueueSnackbar(
        t(
          "The settings could not be saved. Please reload the page and try again."
        ),
        { variant: "error" }
      );
    }
  };

  const handleRotateToken = async () => {
    if (!newToken) return;
    try {
      await rotateTokenMutation({ variables: { newToken } });
      setNewToken("");
      enqueueSnackbar(t("Token rotated successfully"), {
        variant: "success",
      });
    } catch (e) {
      enqueueSnackbar(
        integrationErrorMessage(
          e,
          t("Something went wrong. Please try again.", { ns: "Global" })
        ),
        {
          variant: "error",
        }
      );
    }
  };

  const handleDisconnect = async () => {
    try {
      await removeMutation({
        variables: {
          input: { integrationId: integrationId as IntegrationId },
        },
        refetchQueries: [
          namedOperations.Query.IntegrationsOverview,
          namedOperations.Query.OrganisationIntegrationsSidebar,
        ],
        awaitRefetchQueries: true,
      });
      navigate(INTEGRATIONS_BASE_PATH);
    } catch (e) {
      enqueueSnackbar(
        integrationErrorMessage(
          e,
          t("Something went wrong. Please try again.", { ns: "Global" })
        ),
        {
          variant: "error",
        }
      );
    }
  };

  return (
    <Page title="PDS" breadcrumbs={breadcrumbs} breadcrumbsSize="medium">
      <PageContainer>
        <Stack spacing={2} sx={{ width: "100%", maxWidth: 640 }}>
          <StatusHeader
            health={orgIntegration.health ?? null}
            integrationId={integrationId}
            t={t}
          />

          {/* Section cards — approval → schedule → data; connection last
              (below, outside the forms). Each card is an independent form
              with its own Save/Cancel: a card's save merges its slice over
              the last saved values, so other cards' unsaved edits stay
              untouched. enableReinitialize resets a card once the refetch
              after save delivers the new stored settings. */}
          <Formik<ApprovalValues>
            initialValues={pickApproval(initialValues)}
            onSubmit={values => handleSave({ ...initialValues, ...values })}
            enableReinitialize
          >
            {fp => (
              <Form>
                <FormDirtyTracker
                  onDirtyChange={dirtyOf("approval")}
                  dirty={fp.dirty}
                />
                <RoutingSection fp={fp} t={t} integrationId={integrationId} />
              </Form>
            )}
          </Formik>

          <Formik<QuoteExportValues>
            initialValues={pickQuoteExport(initialValues)}
            onSubmit={values => handleSave({ ...initialValues, ...values })}
            enableReinitialize
          >
            {fp => (
              <Form>
                <FormDirtyTracker
                  onDirtyChange={dirtyOf("quoteExport")}
                  dirty={fp.dirty}
                />
                <QuoteExportSection fp={fp} t={t} />
              </Form>
            )}
          </Formik>

          <Formik<ScheduleValues>
            initialValues={pickSchedule(initialValues)}
            validationSchema={scheduleValidationSchema}
            onSubmit={values => handleSave({ ...initialValues, ...values })}
            enableReinitialize
          >
            {fp => (
              <Form>
                <FormDirtyTracker
                  onDirtyChange={dirtyOf("schedule")}
                  dirty={fp.dirty}
                />
                <ScheduleSection fp={fp} t={t} />
              </Form>
            )}
          </Formik>

          <Formik<ScopeValues>
            initialValues={pickScope(initialValues)}
            validationSchema={scopeValidationSchema}
            onSubmit={values => handleSave({ ...initialValues, ...values })}
            enableReinitialize
          >
            {fp => (
              <Form>
                <FormDirtyTracker
                  onDirtyChange={dirtyOf("scope")}
                  dirty={fp.dirty}
                />
                <SyncScopeSection fp={fp} t={t} />
              </Form>
            )}
          </Formik>

          {/* Verbindung — immediate actions, outside the form-save flow,
              last settings section per the design review */}
          <ConnectionCard
            baseUrl={storedSettings.baseUrl ?? ""}
            newToken={newToken}
            setNewToken={setNewToken}
            onRotateToken={handleRotateToken}
            diagnostics={diagnostics}
            t={t}
          />

          {/* Disconnect — shared card pattern (SettingsPageLayout style) */}
          <CardContainer title={t("Disconnect")}>
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                px: 1,
                pt: 0.5,
                pb: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t(
                  "No longer using this integration? You can disconnect it here."
                )}
              </Typography>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setConfirmOpen(true)}
                disabled={removeLoading}
              >
                {t("Disconnect")}
              </Button>
            </Stack>
          </CardContainer>
        </Stack>
      </PageContainer>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          {t("Disconnect {name}?", { name: "PDS", ns: "Integrations" })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              "Synchronization will stop. Contacts already transferred remain in MeisterSystems. Open review tasks are discarded. To reconnect you will need your PDS access token."
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setConfirmOpen(false)}>
            {t("Cancel")}
          </Button>
          <Button
            color="error"
            onClick={() => {
              setConfirmOpen(false);
              handleDisconnect();
            }}
          >
            {t("Disconnect")}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};

/** Reports Formik dirty state upward for the unsaved-changes route guard */
